import { FC } from 'react';
import { TbChartPie, TbScale } from 'react-icons/tb';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownButtonSelect from '@/shared/components/dropdowns/DropdownButton/DropdownButtonSelect';
import { DropdownMenuLabel } from '@/shared/components/dropdowns/DropdownPanel';
import { FinancialValue, formatFinancialValue, useFinancialValueDisplay } from '@/shared/components/financial-values';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { CustomRechartsUsageAnalyticsTooltip } from '@/shared/components/tooltips';
import { dayjs } from '@/utilities';

import { useFinancialOverviewViewPreference } from '../../hooks/useFinancialOverviewViewPreference';
import { FinancialOverviewCapitalStructureData as CapitalStructureData } from '../../types';
import { FinancialMetricFormulaTooltip } from '../FinancialMetricFormula';
import {
    createFinancialFormula,
    financialFormulaOperand,
    financialFormulaOperator,
} from '../FinancialMetricFormula/formula';
import { FinancialOverviewMetricCardGrid } from '../FinancialOverviewMetricCardGrid';
import { FinancialOverviewPanel } from '../FinancialOverviewPanel';

interface FinancialOverviewCapitalStructureProps {
    preferenceKey?: string;
    section?: CapitalStructureData;
}

type CapitalStructureView = 'bridge' | 'mix';

const isCapitalStructureView = (value: unknown): value is CapitalStructureView => value === 'bridge' || value === 'mix';

const enterpriseValueFormula = createFinancialFormula(
    financialFormulaOperand('marketCap', 'Market Capitalization', 'valuation'),
    financialFormulaOperator('+'),
    financialFormulaOperand('totalDebt', 'Total Debt', 'balance-sheet'),
    financialFormulaOperator('−'),
    financialFormulaOperand('cashAndEquivalents', 'Cash + Equivalents', 'balance-sheet'),
);

export const FinancialOverviewCapitalStructure: FC<FinancialOverviewCapitalStructureProps> = ({
    preferenceKey = 'default',
    section,
}) => {
    const { token } = useAntDesignTheme();
    const { preference: financialValueDisplay } = useFinancialValueDisplay();
    const formatCurrency = (value: number) => formatFinancialValue(value, financialValueDisplay);
    const [view, setView] = useFinancialOverviewViewPreference(
        preferenceKey,
        'capitalStructure',
        'bridge',
        isCapitalStructureView,
    );
    const hasData = Boolean(
        section &&
        [section.marketCap, section.totalDebt, section.cashAndEquivalents, section.enterpriseValue].some(
            (value) => Number.isFinite(value) && value !== 0,
        ),
    );
    const chartData = section
        ? view === 'bridge'
            ? [
                  { fill: token.blue2, label: 'Market Cap', stroke: token.blue4, value: section.marketCap },
                  { fill: token.gold2, label: 'Total Debt', stroke: token.gold5, value: section.totalDebt },
                  {
                      fill: token.green2,
                      label: 'Cash Offset',
                      stroke: token.green5,
                      value: section.cashAndEquivalents,
                  },
                  {
                      fill: token.purple2,
                      label: 'Enterprise Value',
                      stroke: token.purple5,
                      value: section.enterpriseValue,
                  },
              ]
            : [
                  {
                      fill: token.blue2,
                      label: 'Equity Capital',
                      stroke: token.blue4,
                      value: section.equityShare ?? 0,
                  },
                  {
                      fill: token.gold2,
                      label: 'Debt Capital',
                      stroke: token.gold5,
                      value: section.debtShare ?? 0,
                  },
              ]
        : [];
    const asOf = section?.date && dayjs(section.date).isValid() ? dayjs(section.date).format('MMM D, YYYY') : null;

    return (
        <FinancialOverviewPanel
            actions={
                <div style={{ maxWidth: '100%', width: '11rem' }}>
                    <DropdownButtonSelect<CapitalStructureView>
                        aria-label='Select capital structure view'
                        header={<DropdownMenuLabel icon={<TbChartPie size='0.875rem' />}>Display</DropdownMenuLabel>}
                        matchTriggerWidth={false}
                        menuMinWidth='15rem'
                        onChange={(value) => value && setView(value)}
                        options={[
                            {
                                description: 'Compare the valuation components that reconcile to enterprise value.',
                                icon: <TbScale />,
                                label: 'Valuation Bridge',
                                value: 'bridge',
                            },
                            {
                                description: 'Review the relative equity and debt contribution to gross capital.',
                                icon: <TbChartPie />,
                                label: 'Capital Mix',
                                value: 'mix',
                            },
                        ]}
                        placement='bottomRight'
                        size='small'
                        value={view}
                    />
                </div>
            }
            description={`Understand how equity value, debt, and liquidity reconcile to enterprise value${asOf ? ` as of ${asOf}` : ''}.`}
            icon={<TbScale style={{ verticalAlign: '-0.125rem' }} />}
            title='Capital Structure'
        >
            {hasData && section ? (
                <div style={{ display: 'grid', gap: '1rem' }}>
                    <FinancialOverviewMetricCardGrid
                        showDescriptionTooltips
                        items={[
                            {
                                description: 'The aggregate market value of outstanding equity.',
                                key: 'market-cap',
                                label: 'Market Cap',
                                value: <FinancialValue value={section.marketCap} />,
                            },
                            {
                                description: 'Current and long-term interest-bearing obligations.',
                                key: 'total-debt',
                                label: 'Total Debt',
                                value: <FinancialValue value={section.totalDebt} />,
                            },
                            {
                                description: 'Cash and cash-equivalent liquidity that offsets enterprise value.',
                                key: 'cash',
                                label: 'Cash & Equivalents',
                                value: <FinancialValue value={section.cashAndEquivalents} />,
                            },
                            {
                                description:
                                    'The value of the operating business across equity, debt, and available liquidity.',
                                key: 'enterprise-value',
                                label: 'Enterprise Value',
                                tooltip: (
                                    <FinancialMetricFormulaTooltip
                                        description='Reconciles the value of the operating business across equity, debt, and liquidity.'
                                        formula={enterpriseValueFormula}
                                        label='Enterprise Value'
                                    />
                                ),
                                value: <FinancialValue value={section.enterpriseValue} />,
                            },
                        ]}
                    />
                    <div
                        aria-label={`${view === 'bridge' ? 'Enterprise value bridge' : 'Capital mix'} chart`}
                        style={{ height: '15rem', minWidth: 0, width: '100%' }}
                    >
                        <ResponsiveContainer height='100%' width='100%'>
                            <BarChart data={chartData} margin={{ bottom: 8, left: 0, right: 8, top: 8 }}>
                                <CartesianGrid
                                    stroke={token.colorBorderSecondary}
                                    strokeDasharray='3 3'
                                    vertical={false}
                                />
                                <XAxis
                                    axisLine={false}
                                    dataKey='label'
                                    fontSize={12}
                                    stroke={token.colorTextTertiary}
                                    tickLine={false}
                                />
                                <YAxis
                                    axisLine={false}
                                    fontSize={12}
                                    stroke={token.colorTextTertiary}
                                    tickFormatter={(value: number) =>
                                        view === 'bridge' ? formatCurrency(value) : `${Math.round(value * 100)}%`
                                    }
                                    tickLine={false}
                                    width={72}
                                />
                                <Tooltip
                                    content={
                                        <CustomRechartsUsageAnalyticsTooltip
                                            labelFormatter={(label) => String(label)}
                                            nameFormatter={() =>
                                                view === 'bridge' ? 'Reported Value' : 'Share of Gross Capital'
                                            }
                                            valueFormatter={(value) =>
                                                view === 'bridge'
                                                    ? formatCurrency(Number(value))
                                                    : `${(Number(value) * 100).toFixed(1)}%`
                                            }
                                        />
                                    }
                                    cursor={{ fill: token.colorFillTertiary }}
                                />
                                <Bar
                                    dataKey='value'
                                    maxBarSize={72}
                                    radius={[token.borderRadius, token.borderRadius, 0, 0]}
                                >
                                    {chartData.map((item) => (
                                        <Cell fill={item.fill} key={item.label} stroke={item.stroke} strokeWidth={1} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            ) : (
                <EmptySection
                    description='Capitalization, debt, and liquidity data are not currently available for this company.'
                    height='16rem'
                    icon={<TbScale size='1.25rem' />}
                    label='Capital Structure Unavailable'
                />
            )}
        </FinancialOverviewPanel>
    );
};
